import type { Session } from '@ms-cloudpack/common-types';
import { getCachePath } from '@ms-cloudpack/config';
import type { TaskReporter } from '@ms-cloudpack/task-reporter';
import prompts from 'prompts';
import type { CommandExitParams } from '../../types/CommandAction.js';
import { getActiveSessions } from './getActiveSessions.js';

type SessionWithRequiredApiServer = Session & { urls: { apiServer: string } };

/**
 * Get a single active session to link packages.
 * The session must have an API server URL.
 */
export async function getSessionToLink(options: {
  reporter: TaskReporter;
  /** Cache path from CLI options */
  cachePath: string | undefined;
}): Promise<CommandExitParams & { session?: SessionWithRequiredApiServer }> {
  const { reporter } = options;

  // Find the list of running sessions.
  const cachePath = await getCachePath(options.cachePath);
  const runningSessions = await getActiveSessions({ reporter, cachePath });

  if (runningSessions.length === 0) {
    return {
      hasErrors: true,
      message: 'No running sessions found. Please start a session using `cloudpack start` first.',
    };
  }

  console.debug(`Running sessions: ${runningSessions.map((session) => session.id).join(', ')}`);

  // Select a session to link packages.
  let session: Session | undefined;

  if (runningSessions.length === 1) {
    // Only one session, so use it automatically.
    session = runningSessions[0];
  } else {
    // Prompt the user to select a session.
    const response = await prompts({
      type: 'select',
      name: 'value',
      message: 'Select the app you want to link',
      choices: runningSessions
        .map((s) => ({
          title: s.projectName,
          description: s.config.appPath + ' ' + s.id,
          value: s,
        }))
        .sort((s1, s2) => s1.title.localeCompare(s2.title)),
    });

    // value will be undefined if the user presses Ctrl+C
    session = response.value as Session | undefined;
  }

  if (!session) {
    return { isInterrupted: true, message: '' };
  }

  console.debug('Selected session:', session.id);

  if (!session.urls.apiServer) {
    // We should never get here, but just in case.
    return { hasErrors: true, message: 'The selected session does not have an API server URL.' };
  }

  return {
    session: session as SessionWithRequiredApiServer,
    message: '', // required by the type but not used
  };
}
